import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { AdministrationWorkspacesModal } from "@/features/administration/components/AdministrationWorkspacesModal";
import { RoleSessionWatch } from "@/features/auth/RoleSessionWatch";
import { useAdminWorkspacesModalStore } from "@/stores/adminWorkspacesModalStore";
import { useWorkspaceStore } from "@/state/workspace.store";
import { request } from "@/lib/api";

const WORKSPACES_Q = "workspaces";

const FEEDBACK_TYPES = [
  { value: "bug", label: "Bug" },
  { value: "friction", label: "Friction" },
  { value: "idea", label: "Idea" },
] as const;

type FeedbackType = (typeof FEEDBACK_TYPES)[number]["value"];

/** /gates/:submissionId/review — the day-6 approver screen, 60-second DoD. */
const GATE_REVIEW = /^\/gates\/([0-9a-fA-F-]{36})\/review\/?$/;

/**
 * Pilot feedback widget.
 *
 * ⚠️ Mounted HERE, not in DashboardLayout. The gate review route is declared
 * OUTSIDE DashboardLayout on purpose (App.tsx: "that shell is workspace-shaped
 * and this caller may hold no workspace membership at all"), so a
 * DashboardLayout mount would not reach it. This chrome is an ancestor of both
 * that route and every DashboardLayout route.
 *
 * ⚠️ The feedback card is inline. App.tsx mounts one <Toaster /> for the rest of
 * the product, so toast.success / toast.error are visible on every route.
 *
 * ⚠️ Uses `request` from @/lib/api, which attaches the CSRF header
 * automatically on mutating verbs. Calling axios directly, or adding another
 * client, would 403 with CSRF_TOKEN_MISSING.
 *
 * The path is /v1/feedback because the client baseURL already ends in /api;
 * v1RewriteMiddleware maps /api/v1/* back to /api/* server-side, where the
 * controller prefix is plain 'feedback'.
 */
function FeedbackWidgetInline() {
  const location = useLocation();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("friction");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gateId = useMemo(() => {
    const m = GATE_REVIEW.exec(location.pathname);
    return m ? m[1] : undefined;
  }, [location.pathname]);

  useEffect(() => {
    const openForm = () => setOpen(true);
    window.addEventListener("zephix:feedback-open", openForm);
    return () => window.removeEventListener("zephix:feedback-open", openForm);
  }, []);

  const reset = () => {
    setOpen(false);
    setDone(false);
    setError(null);
    setMessage("");
    setLink("");
    setType("friction");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 5 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await request.post("/v1/feedback", {
        type,
        message: message.trim(),
        ...(link.trim() ? { link: link.trim() } : {}),
        ...(activeWorkspaceId ? { workspaceId: activeWorkspaceId } : {}),
        ...(gateId ? { gateId } : {}),
        currentUrl: `${location.pathname}${location.search}`.slice(0, 2048),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      setDone(true);
    } catch {
      setError("Could not send that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="feedback-inline-title"
      className="fixed left-1/2 top-24 z-[80] w-[22rem] max-w-[calc(100vw-3rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
    >
      {done ? (
        // Inline confirmation card. aria-live so it is announced, since there
        // is no toast layer to do it.
        <div aria-live="polite">
          <h2 id="feedback-inline-title" className="text-sm font-semibold text-gray-900">
            Thank you — that reached us.
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            We read every note during the pilot.
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-3 w-full rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={submit} aria-label="Feedback form">
          <div className="flex items-start justify-between">
            <h2 id="feedback-inline-title" className="text-sm font-semibold text-gray-900">
              Send feedback
            </h2>
            <button
              type="button"
              onClick={reset}
              aria-label="Close feedback form"
              className="-mr-1 -mt-1 rounded p-1 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="mt-3 flex gap-1.5" role="group" aria-label="Feedback type">
            {FEEDBACK_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-pressed={type === t.value}
                onClick={() => setType(t.value)}
                className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-medium ${
                  type === t.value
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <label htmlFor="feedback-inline-message" className="mt-3 block text-xs text-gray-500">
            What happened?
          </label>
          <textarea
            id="feedback-inline-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={4000}
            required
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-gray-900 focus:outline-none"
          />

          <label htmlFor="feedback-inline-link" className="mt-2 block text-xs text-gray-500">
            Link to screenshot / Loom (optional)
          </label>
          <input
            id="feedback-inline-link"
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            maxLength={2048}
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-gray-900 focus:outline-none"
          />

          {error ? (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy || message.trim().length < 5}
            className="mt-3 w-full rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-40"
          >
            {busy ? "Sending…" : "Send"}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * Wraps all authenticated routes: global admin UI (e.g. workspaces browser),
 * the pilot feedback widget, and {@link Outlet}.
 */
export default function AppAuthenticatedChrome() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOpen = useAdminWorkspacesModalStore((s) => s.isOpen);
  const close = useAdminWorkspacesModalStore((s) => s.close);

  // Deep link / bookmark: ?workspaces=1 opens modal then strips the param (stay on current path).
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get(WORKSPACES_Q) !== "1") return;
    useAdminWorkspacesModalStore.getState().open();
    sp.delete(WORKSPACES_Q);
    const next = sp.toString();
    navigate({ pathname: location.pathname, search: next ? `?${next}` : "" }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  return (
    <>
      <AdministrationWorkspacesModal isOpen={isOpen} onClose={close} />
      <RoleSessionWatch />
      <FeedbackWidgetInline />
      <Outlet />
    </>
  );
}
